import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { SelectOption } from '../../model/select.option';

/** Uma entrada visível do menu (na ordem renderizada). */
interface MenuEntry {
  kind: 'none' | 'group' | 'add';
  option?: SelectOption;
}

/**
 * `app-group-menu` (P2 do SPEC) — CONTEÚDO BURRO do menu de grupo, projetado dentro
 * de um `app-popover`. Sem Material/CDK (ícones são `<span class="material-symbols-outlined">`
 * puros, sem `mat-icon`). Renderiza
 * `(nenhum)` + chips de grupo + `add`, e EMITE intenções — não persiste nada:
 *   - `select(id|null)` — escolheu um grupo (ou "nenhum");
 *   - `requestNew()`     — pediu criar grupo;
 *   - `requestDelete(nomePublico)` — pediu excluir um grupo (× no chip).
 * A lógica smart (persistência/toasts/dialogs) fica no controlador (P3 = select-input-group).
 *
 * A11y: `role="listbox"` + roving via `aria-activedescendant`; ↑/↓/Home/End navegam,
 * Enter/Espaço seleciona, Delete/Backspace num grupo pede exclusão. O foco do painel
 * (vindo do popover) recai neste container (`tabindex=0`).
 */
@Component({
  selector: 'app-group-menu',
  templateUrl: './group-menu.component.html',
  styleUrls: ['./group-menu.component.scss'],
})
export class GroupMenuComponent implements OnChanges {
  @Input() groups: SelectOption[] = [];
  @Input() selectedId: any = null;
  @Input() indices = { internal: 'id', public: 'value' };
  @Input() prependPublic = '';
  @Input() includeNoneOption = false;
  @Input() disabledDeleteOption = false;
  @Input() disabledNewOption = false;
  /** Nome (ligature) do ícone Material Symbols exibido no chip de grupo. */
  @Input() icon: string;
  /** Chave i18n do rótulo "criar grupo". */
  @Input() addLabel = 'submissions.create.group';
  @Input() boldAdd = false;

  // eslint-disable-next-line @angular-eslint/no-output-native -- `select` é a semântica do menu (item escolhido), não evento DOM nativo
  @Output() select = new EventEmitter<any>();
  @Output() requestNew = new EventEmitter<void>();
  @Output() requestDelete = new EventEmitter<string>();

  entries: MenuEntry[] = [];

  ngOnChanges(): void {
    this.entries = this.buildEntries();
  }

  private buildEntries(): MenuEntry[] {
    const list: MenuEntry[] = [];
    if (this.includeNoneOption) { list.push({ kind: 'none' }); }
    (this.groups || []).forEach(option => list.push({ kind: 'group', option }));
    if (!this.disabledNewOption) { list.push({ kind: 'add' }); }
    return list;
  }

  isSelected(entry: MenuEntry): boolean {
    if (entry.kind === 'none') { return this.selectedId == null; }
    if (entry.kind === 'group') { return entry.option[this.indices.internal] === this.selectedId; }
    return false;
  }

  activate(entry: MenuEntry): void {
    switch (entry.kind) {
      case 'none': this.select.emit(null); break;
      case 'group': this.select.emit(entry.option[this.indices.internal]); break;
      case 'add': this.requestNew.emit(); break;
    }
  }

  /** Clique no × do chip de grupo (para de propagar p/ não acionar o select da linha). */
  onDelete(entry: MenuEntry, ev: Event): void {
    ev.stopPropagation();
    if (entry.kind === 'group') {
      this.requestDelete.emit(entry.option[this.indices.public]);
    }
  }

  /** Delete/Backspace na opção ativa (via jemsListbox) → excluir grupo. */
  onDeleteActive(entry: MenuEntry): void {
    if (!this.disabledDeleteOption && entry && entry.kind === 'group') {
      this.requestDelete.emit(entry.option[this.indices.public]);
    }
  }
}
